import { useState } from 'react'
import type { FormEvent } from 'react'

import type { Contract, ContractCatalog, EagleSLA, MasterDataTutte } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'
import { etichettaPeriodicita, etichettaStatoContratto, etichettaTipoContratto } from './contratti'
import { SelettoreAnagrafica } from './SelettoreAnagrafica'
import { SelettoreFornitore } from './Fornitori'

/**
 * Il modulo del contratto, per crearlo e per modificarlo: le condizioni
 * (date, rinnovo, valore), le imputazioni (centro di costo, unità, sede), chi
 * ne risponde, i promemoria. Voci, documenti e copertura hanno le loro
 * schede nel dettaglio: qui c'è ciò che si firma.
 */
export type ValoriContratto = {
  reference: string
  name: string
  contract_type: string
  status: string
  vendor: string
  vendor_contact: string
  supplier_id: string | null
  start_date: string
  end_date: string
  auto_renew: boolean
  /** CENSO MED: clinical engineering sees it and links its devices to it. */
  med_visible: boolean
  notice_days: string
  renewal_months: string
  value: string
  currency: string
  billing_period: string
  payment_terms: string
  external_ref: string
  service_level: string
  cost_center_id: string | null
  org_unit_id: string | null
  location_id: string | null
  owner: string
  owner_email: string
  eagle_sla_id: string | null
  reminder_days: string
  notes: string
}

export const VALORI_VUOTI: ValoriContratto = {
  reference: '', name: '', contract_type: 'maintenance', status: 'active', vendor: '', vendor_contact: '', supplier_id: null,
  start_date: '', end_date: '', auto_renew: false, med_visible: false, notice_days: '', renewal_months: '', value: '', currency: 'EUR',
  billing_period: 'annual', payment_terms: '', external_ref: '', service_level: '',
  cost_center_id: null, org_unit_id: null, location_id: null, owner: '', owner_email: '', eagle_sla_id: null,
  reminder_days: '', notes: '',
}

export function valoriDa(c: Contract): ValoriContratto {
  return {
    reference: c.reference, name: c.name ?? '', contract_type: c.contract_type, status: c.status,
    vendor: c.vendor, vendor_contact: c.vendor_contact ?? '', supplier_id: c.supplier_id ?? null, start_date: c.start_date ?? '', end_date: c.end_date ?? '',
    auto_renew: c.auto_renew, med_visible: Boolean(c.med_visible), notice_days: c.notice_days === null ? '' : String(c.notice_days),
    renewal_months: c.renewal_months === null ? '' : String(c.renewal_months),
    value: c.value === null ? '' : String(c.value), currency: c.currency, billing_period: c.billing_period,
    payment_terms: c.payment_terms ?? '', external_ref: c.external_ref ?? '', service_level: c.service_level ?? '',
    cost_center_id: c.cost_center_id, org_unit_id: c.org_unit_id, location_id: c.location_id,
    owner: c.owner ?? '', owner_email: c.owner_email ?? '', eagle_sla_id: c.eagle_sla_id,
    reminder_days: c.reminder_days_custom ? c.reminder_days.join(', ') : '', notes: c.notes ?? '',
  }
}

/** Il corpo per l'API: stringhe vuote → null, numeri come numeri. */
export function corpoDa(v: ValoriContratto): Record<string, unknown> {
  const giorni = v.reminder_days.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean).map(Number)
  return {
    reference: v.reference.trim(),
    name: v.name.trim() || null,
    contract_type: v.contract_type,
    status: v.status,
    vendor: v.vendor.trim(),
    vendor_contact: v.vendor_contact.trim() || null,
    supplier_id: v.supplier_id,
    start_date: v.start_date || null,
    end_date: v.end_date || null,
    auto_renew: v.auto_renew,
    med_visible: v.med_visible,
    notice_days: v.notice_days ? Number(v.notice_days) : null,
    renewal_months: v.renewal_months ? Number(v.renewal_months) : null,
    value: v.value ? Number(v.value) : null,
    currency: (v.currency || 'EUR').toUpperCase().slice(0, 3),
    billing_period: v.billing_period,
    payment_terms: v.payment_terms.trim() || null,
    external_ref: v.external_ref.trim() || null,
    service_level: v.service_level.trim() || null,
    cost_center_id: v.cost_center_id,
    org_unit_id: v.org_unit_id,
    location_id: v.location_id,
    owner: v.owner.trim() || null,
    owner_email: v.owner_email.trim() || null,
    eagle_sla_id: v.eagle_sla_id,
    reminder_days: giorni.length > 0 && giorni.every((n) => Number.isInteger(n)) ? giorni : [],
    notes: v.notes.trim() || null,
  }
}

export function ModuloContratto({
  iniziale, catalogo, anagrafiche, slas, nuovo, onSalva, onAnnulla,
}: {
  iniziale: ValoriContratto
  catalogo: ContractCatalog | null
  anagrafiche: MasterDataTutte | null
  slas: EagleSLA[]
  nuovo: boolean
  onSalva: (corpo: Record<string, unknown>) => Promise<void>
  onAnnulla: () => void
}) {
  const [v, setV] = useState<ValoriContratto>(iniziale)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const set = <K extends keyof ValoriContratto>(k: K) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })

  async function invia(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErrore(null)
    try {
      await onSalva(corpoDa(v))
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  const promemoriaDefault = (catalogo?.default_reminder_days ?? [90, 30, 7]).join(', ')

  return (
    <form onSubmit={invia} className="eagle-form contratto-form">
      <ErrorBanner error={errore} />
      <div className="eagle-report__riga">
        <label className="field"><span className="field__label">{t('Riferimento')}</span>
          <input value={v.reference} onChange={set('reference')} required className="mono" />
          <p className="field__hint">{t('Come lo chiamano le due parti: è quello che si cita in una richiesta di intervento, e senza il fornitore non trova nulla.')}</p></label>
        <label className="field"><span className="field__label">{t('Nome')}</span>
          <input value={v.name} onChange={set('name')} /></label>
        <label className="field" style={{ maxWidth: 260 }}><span className="field__label">{t('Tipo')}</span>
          <select value={v.contract_type} onChange={set('contract_type')}>
            {(catalogo?.types ?? []).map((x) => <option key={x.value} value={x.value}>{etichettaTipoContratto(x.value)}</option>)}
          </select></label>
        {!nuovo && (
          <label className="field" style={{ maxWidth: 200 }}><span className="field__label">{t('Stato')}</span>
            <select value={v.status} onChange={set('status')}>
              {(catalogo?.statuses ?? []).map((x) => <option key={x.value} value={x.value}>{etichettaStatoContratto(x.value)}</option>)}
            </select>
            <p className="field__hint">{t('«Scaduto» lo decidono le date; rinnovo e cessazione hanno i loro pulsanti, che scrivono lo storico.')}</p></label>
        )}
      </div>

      <div className="eagle-report__riga">
        <label className="field"><span className="field__label">{t('Fornitore')}</span>
          <input value={v.vendor} onChange={set('vendor')} required />
          <SelettoreFornitore value={v.supplier_id} onChange={(id) => setV({ ...v, supplier_id: id })} />
          <p className="field__hint">{t('Il collegamento al registro fornitori: CENSO lo fa da solo se il nome è già noto.')}</p></label>
        <label className="field"><span className="field__label">{t('Contatto del fornitore')}</span>
          <input value={v.vendor_contact} onChange={set('vendor_contact')} placeholder={t('nome, telefono, email del supporto')} />
          <p className="field__hint">{t('Sembra un dettaglio da rubrica, ed è la prima cosa che si cerca alle tre di notte.')}</p></label>
        <label className="field"><span className="field__label">{t('Livello di servizio')}</span>
          <input value={v.service_level} onChange={set('service_level')} placeholder={t('NBD, 4h lavorative…')} /></label>
        <label className="field" style={{ maxWidth: 260 }}><span className="field__label">{t('SLA di Eagle')}</span>
          <select value={v.eagle_sla_id ?? ''} onChange={(e) => setV({ ...v, eagle_sla_id: e.target.value || null })} disabled={slas.length === 0}>
            <option value="">{slas.length === 0 ? t('nessuno definito') : t('nessuno')}</option>
            {slas.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.availability_pct}%</option>)}
          </select>
          <p className="field__hint">{t('Chiude il cerchio fra ciò che si è comprato e ciò che Eagle misura.')}</p></label>
      </div>

      <div className="eagle-report__riga">
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Decorrenza')}</span>
          <input type="date" value={v.start_date} onChange={set('start_date')} /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Scadenza')}</span>
          <input type="date" value={v.end_date} onChange={set('end_date')} /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Durata del rinnovo (mesi)')}</span>
          <input type="number" min="1" max="120" value={v.renewal_months} onChange={set('renewal_months')} placeholder="12" />
          <p className="field__hint">{t('Il pulsante «Rinnova» la usa per proporre la nuova scadenza; con il rinnovo tacito la scadenza avanza da sola.')}</p></label>
      </div>

      <label className="eagle-interruttore">
        <input type="checkbox" checked={v.auto_renew} onChange={(e) => setV({ ...v, auto_renew: e.target.checked })} />{' '}
        {t('Rinnovo tacito')}
        <span className="muted"> — {t('Cambia il significato della scadenza: con il rinnovo automatico quella data è l’ultimo giorno per disdire, non quello in cui si resta scoperti.')}</span>
      </label>
      <label className="eagle-interruttore">
        <input type="checkbox" checked={v.med_visible} onChange={(e) => setV({ ...v, med_visible: e.target.checked })} />{' '}
        {t('Visibile all’Ingegneria clinica')}
        <span className="muted"> — {t('Il ruolo «Ingegneria clinica» lo vede in CENSO MED e ci collega gli elettromedicali (per esempio un contratto di manutenzione). Gli altri contratti non li vede.')}</span>
      </label>
      {v.auto_renew && (
        <label className="field" style={{ maxWidth: 220 }}><span className="field__label">{t('Giorni di preavviso')}</span>
          <input type="number" min="0" max="365" value={v.notice_days} onChange={set('notice_days')} />
          <p className="field__hint">{t('Senza questo dato la data mostrata coincide con la scadenza, e si crede di avere tempo fino a quel giorno.')}</p></label>
      )}

      <div className="eagle-report__riga">
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Valore')}</span>
          <input type="number" step="0.01" min="0" value={v.value} onChange={set('value')} /></label>
        <label className="field" style={{ maxWidth: 110 }}><span className="field__label">{t('Valuta')}</span>
          <input value={v.currency} onChange={set('currency')} maxLength={3} className="mono" /></label>
        <label className="field" style={{ maxWidth: 190 }}><span className="field__label">{t('Periodicità')}</span>
          <select value={v.billing_period} onChange={set('billing_period')}>
            {(catalogo?.billing_periods ?? []).map((x) => <option key={x.value} value={x.value}>{etichettaPeriodicita(x.value)}</option>)}
          </select>
          <p className="field__hint">{t("Senza, un valore di 12.000 non dice se è il canone dell'anno o del mese.")}</p></label>
        <label className="field"><span className="field__label">{t('Termini di pagamento')}</span>
          <input value={v.payment_terms} onChange={set('payment_terms')} placeholder={t('30 gg d.f.f.m., anticipato…')} /></label>
        <label className="field"><span className="field__label">{t('Riferimento esterno')}</span>
          <input value={v.external_ref} onChange={set('external_ref')} placeholder={t('ordine, pratica acquisti, id nel gestionale')} className="mono" /></label>
      </div>

      <div className="eagle-report__riga">
        <label className="field"><span className="field__label">{t('Centro di costo')}</span>
          <SelettoreAnagrafica kind="cost_center" voci={anagrafiche?.cost_center ?? []} value={v.cost_center_id} onChange={(id) => setV({ ...v, cost_center_id: id })} /></label>
        <label className="field"><span className="field__label">{t('Unità organizzativa')}</span>
          <SelettoreAnagrafica kind="org_unit" voci={anagrafiche?.org_unit ?? []} value={v.org_unit_id} onChange={(id) => setV({ ...v, org_unit_id: id })} /></label>
        <label className="field"><span className="field__label">{t('Sede')}</span>
          <SelettoreAnagrafica kind="location" voci={anagrafiche?.location ?? []} value={v.location_id} onChange={(id) => setV({ ...v, location_id: id })} /></label>
      </div>

      <div className="eagle-report__riga">
        <label className="field"><span className="field__label">{t('Responsabile')}</span>
          <input value={v.owner} onChange={set('owner')} /></label>
        <label className="field"><span className="field__label">{t('Email del responsabile')}</span>
          <input type="email" value={v.owner_email} onChange={set('owner_email')} />
          <p className="field__hint">{t('Riceve i promemoria di scadenza oltre ai destinatari del tenant.')}</p></label>
        <label className="field" style={{ maxWidth: 260 }}><span className="field__label">{t('Promemoria (giorni prima)')}</span>
          <input value={v.reminder_days} onChange={set('reminder_days')} placeholder={promemoriaDefault} className="mono" />
          <p className="field__hint">{t('Vuoto = {giorni}. Un avviso per soglia, prima della data che conta.', { giorni: promemoriaDefault })}</p></label>
      </div>

      <label className="field"><span className="field__label">{t('Note')}</span>
        <textarea rows={3} value={v.notes} onChange={set('notes')} /></label>

      <div className="eagle-form__azioni">
        <button type="submit" disabled={busy || !v.reference.trim() || !v.vendor.trim()}>
          {busy ? t('Salvo…') : nuovo ? t('Registra contratto') : t('Salva')}
        </button>
        <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
      </div>
    </form>
  )
}
